import dayjs from "dayjs";
import { VIEWPOINT_FEED_CACHE } from "./Config";
import { mergeFeedIncoming, sortViewpointFeedByTimeDesc } from "./ViewpointUtil";
import type {
  ViewpointFeedCacheScope,
  ViewpointFeedCacheSnapshot,
  ViewpointFeedPost,
} from "./ViewpointFeedTypes";

const DT = "YYYY-MM-DD HH:mm:ss";

function cacheStorageKey(scope: ViewpointFeedCacheScope): string {
  const cat = scope.categoryId ?? "all";
  const kind = scope.listKind ?? "all";
  return `${VIEWPOINT_FEED_CACHE.keyPrefix}.v${VIEWPOINT_FEED_CACHE.version}:${scope.customerId}:${scope.spaceId}:${scope.spaceProductId}:${cat}:${kind}`;
}

function toCachePublishedAt(v: string | Date): string {
  if (v instanceof Date) {
    return dayjs(v).format(DT);
  }
  if (typeof v === "string") {
    const t = v.trim();
    const d = dayjs(t);
    return d.isValid() ? d.format(DT) : t;
  }
  return "";
}

function serializePostForCache(p: ViewpointFeedPost): ViewpointFeedPost {
  return {
    ...p,
    publishedAt: toCachePublishedAt(p.publishedAt),
    likeCount: p.likeCount ?? 0,
    collectCount: p.collectCount ?? 0,
  };
}

function serializeLists(pinned: ViewpointFeedPost[], feed: ViewpointFeedPost[]) {
  return {
    pinned: pinned.map(serializePostForCache),
    feed: feed.map(serializePostForCache),
  };
}

/** 保留 Feed 最新 N 条（置顶区完整保留，至多 3 条） */
export function trimViewpointFeedStream(feed: ViewpointFeedPost[]): ViewpointFeedPost[] {
  const ordered = sortViewpointFeedByTimeDesc(feed);
  if (ordered.length <= VIEWPOINT_FEED_CACHE.maxItems) {
    return ordered;
  }
  return ordered.slice(0, VIEWPOINT_FEED_CACHE.maxItems);
}

function writeSnapshot(snapshot: ViewpointFeedCacheSnapshot): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const serialized = serializeLists(snapshot.pinned, snapshot.feed);
  const trimmedFeed = trimViewpointFeedStream(serialized.feed);
  const wasTrimmed = trimmedFeed.length < serialized.feed.length;
  const toSave: ViewpointFeedCacheSnapshot = {
    ...snapshot,
    version: VIEWPOINT_FEED_CACHE.version,
    pinned: serialized.pinned,
    feed: trimmedFeed,
    hasMoreOlder: snapshot.hasMoreOlder || wasTrimmed,
    updatedAt: dayjs().format(DT),
  };
  try {
    localStorage.setItem(cacheStorageKey(snapshot), JSON.stringify(toSave));
  } catch {
    if (trimmedFeed.length <= VIEWPOINT_FEED_CACHE.maxItems / 2) {
      return;
    }
    const half = trimViewpointFeedStream(trimmedFeed.slice(0, Math.floor(trimmedFeed.length / 2)));
    try {
      localStorage.setItem(
        cacheStorageKey(snapshot),
        JSON.stringify({
          ...toSave,
          feed: half,
          hasMoreOlder: true,
        })
      );
    } catch {
      // ignore quota errors
    }
  }
}

export function loadViewpointFeedCache(
  scope: ViewpointFeedCacheScope
): ViewpointFeedCacheSnapshot | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  const raw = localStorage.getItem(cacheStorageKey(scope));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as ViewpointFeedCacheSnapshot;
    if (parsed.version !== VIEWPOINT_FEED_CACHE.version) {
      localStorage.removeItem(cacheStorageKey(scope));
      return null;
    }
    if (
      parsed.customerId !== scope.customerId ||
      parsed.spaceId !== scope.spaceId ||
      parsed.spaceProductId !== scope.spaceProductId ||
      (parsed.categoryId ?? null) !== (scope.categoryId ?? null) ||
      (parsed.listKind ?? "all") !== (scope.listKind ?? "all")
    ) {
      return null;
    }
    if (!Array.isArray(parsed.pinned) || !Array.isArray(parsed.feed)) {
      return null;
    }
    const serialized = serializeLists(parsed.pinned, parsed.feed);
    return {
      ...parsed,
      pinned: serialized.pinned,
      feed: serialized.feed,
      hasMoreOlder: Boolean(parsed.hasMoreOlder),
    };
  } catch {
    localStorage.removeItem(cacheStorageKey(scope));
    return null;
  }
}

export function buildViewpointFeedCacheSnapshot(
  scope: ViewpointFeedCacheScope,
  pinned: ViewpointFeedPost[],
  feed: ViewpointFeedPost[],
  hasMoreOlder: boolean,
  refreshedAt: Date | null
): ViewpointFeedCacheSnapshot {
  return {
    version: VIEWPOINT_FEED_CACHE.version,
    customerId: scope.customerId,
    spaceId: scope.spaceId,
    spaceProductId: scope.spaceProductId,
    categoryId: scope.categoryId,
    listKind: scope.listKind,
    pinned,
    feed,
    hasMoreOlder,
    refreshedAt: refreshedAt ? dayjs(refreshedAt).format(DT) : null,
    updatedAt: dayjs().format(DT),
  };
}

export function saveViewpointFeedCache(
  scope: ViewpointFeedCacheScope,
  pinned: ViewpointFeedPost[],
  feed: ViewpointFeedPost[],
  hasMoreOlder: boolean,
  refreshedAt: Date | null
): void {
  writeSnapshot(buildViewpointFeedCacheSnapshot(scope, pinned, feed, hasMoreOlder, refreshedAt));
}

/** 增量合并 Feed 并写入本地 */
export function saveViewpointFeedIncremental(
  scope: ViewpointFeedCacheScope,
  pinned: ViewpointFeedPost[],
  existingFeed: ViewpointFeedPost[],
  incomingFeed: ViewpointFeedPost[],
  hasMoreOlder: boolean,
  refreshedAt: Date | null
): { pinned: ViewpointFeedPost[]; feed: ViewpointFeedPost[]; hasMoreOlder: boolean } {
  const mergedFeed = mergeFeedIncoming(existingFeed, incomingFeed);
  const trimmedFeed = trimViewpointFeedStream(mergedFeed);
  const wasTrimmed = trimmedFeed.length < mergedFeed.length;
  const nextHasMoreOlder = hasMoreOlder || wasTrimmed;
  writeSnapshot(
    buildViewpointFeedCacheSnapshot(scope, pinned, trimmedFeed, nextHasMoreOlder, refreshedAt)
  );
  return { pinned, feed: trimmedFeed, hasMoreOlder: nextHasMoreOlder };
}

export function updateViewpointFeedCacheItem(
  scope: ViewpointFeedCacheScope,
  viewpointId: string,
  partial: Partial<ViewpointFeedPost>,
  pinned: ViewpointFeedPost[],
  feed: ViewpointFeedPost[]
): void {
  const nextPinned = pinned.map((row) =>
    row.id === viewpointId ? { ...row, ...partial } : row
  );
  const nextFeed = feed.map((row) =>
    row.id === viewpointId ? { ...row, ...partial } : row
  );
  const cached = loadViewpointFeedCache(scope);
  writeSnapshot({
    ...(cached ?? buildViewpointFeedCacheSnapshot(scope, nextPinned, nextFeed, false, null)),
    pinned: nextPinned,
    feed: nextFeed,
  });
}

export function clearViewpointFeedCache(scope: ViewpointFeedCacheScope): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  localStorage.removeItem(cacheStorageKey(scope));
}
